import React, { useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import FormControl from '../../../../../Components/Form/FormControl';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import { getDate } from '../../../../../Helpers/date.helper';
import {
	WritePatientElectronicPrescription,
	WritePatientElectronicPrescriptionParams,
} from '../../../Commands/WritePatientElectronicPrescription';
import { useNCZIContext } from '../../../Services/NCZIContext';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import FormTitle from '../../../../../Components/Form/FormTitle';
import InputField from '../../../../../Components/Form/components/InputField';
import FormContainer from '../../../../../Components/Form/FormContainer';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import {
	DOSAGE_TYPE_PEV,
	DOSAGE_TYPE_VAR,
} from '../../../Interfaces/ncziElectronicPrescriptionItem';
import FormCondition from '../../../../../Components/Form/FormCondition';
import { useForm } from 'react-hook-form';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import RadioListField from '../../../../../Components/Form/components/RadioListField';
import { useEffectAsync } from '../../../../../helpers';
import {
	useLazyNcziListEnumItemsQuery,
	useLazyNcziRefrenceItemByAbbrQuery,
	useLazyNcziRefrenceItemQuery,
	useLazyNcziRefrenceItemsByCodeQuery,
} from '../../../../../graphql/graphql';
import NCZIReferenceSearchSelectField from '../../ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import moment, { Moment } from 'moment';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import SelectField from '../../../../../Components/Form/components/SelectField';
import { NCZIReference } from '../../../Interfaces/ncziTypes';
import { NCZIInsuranceMedicamentValidator } from '../../InsuranceMedicamentSearchSelect/NCZIInsuranceMedicamentSearchSelect';
import { Command } from '../../../Commands/Command';
import { VerifyPatientInteractions } from '../../../Commands/VerifyPatientInteractions';
import NcziHtmlModalViewer from '../../NcziHtmlModalViewer/NcziHtmlModalViewer';
import { useLoadProfile } from '../../../../../Components/PatientProfile/PatientProfileDataLoader';
import { usePatientTreatmentContext } from '../../../../Patient/Components/PatientTreatment/ContextProvider';
import ElectronicPrescriptionCreatedDetailModal from '../CreatedDetail/ElectronicPrescriptionCreatedDetailModal';
import ElectronicPrescriptionCreateFail from './ElectronicPrescriptionCreateFail';
import useNotification from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';
import NCZIProductSearchSelectField from '../../ProductSearchSelect/NCZIProductSearchSelectField';
import {
	NCZIProductSelectItem,
	NCZIProductValidator,
} from '../../ProductSearchSelect/NCZIProductSearchSelect';
import { SearchMedicaments } from '../../../Commands/SearchMedicaments';
import ElectronicPrescriptionCreateWithoutCard from './ElectronicPrescriptionCreateWithoutCard';
import { FormValues } from '../../../../MagicForm/Interfaces/FormValues';

import styles from './ElectronicPrescriptionCreateForm.module.scss';
import IndicationLimitationCodeSelectField from '../../IndicationLimitationCodeSelect/IndicationLimitationCodeSelectField';
import { IndicationLimitationCodeSelectOption } from '../../IndicationLimitationCodeSelect/IndicationLimitationCodeSelect';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import FormMultiplierRemoveButton from '../../../../../Components/Form/FormMultiplierRemoveButton';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import Text from '../../../../../Components/Base/Text/Text';

export interface ElectronicPrescriptionCreateFormValues extends FormValues {
	medicamentType: string;
	iplType: NCZIReferenceSelectItem | undefined;
	ipl: {
		composition: string;
		prepareInstruction: string;
		useInstruction: string;
	};
	urgentEmergency: boolean;
	nonPayer: boolean;
	prohibitionOfReplacement: boolean;
	prohibitionOfReplacementCode: NCZIProductSelectItem | undefined;
	forcedEntry: boolean;
	productOnException: boolean;
	newbornToParentBN: boolean;
	permanentlyAffected: boolean;
	reportedDate: Moment;
	reportedQuantity: string;
	reportedDiagnosis: NCZIReferenceSelectItem | undefined;
	product: NCZIProductSelectItem | undefined;
	reportedATC: NCZIReferenceSelectItem | undefined;
	deviceUnit: NCZIReferenceSelectItem | undefined;
	deviceDescription: string;
	deviceJustification: string;
	atcAddon: string;
	dosage: {
		daily: {
			type: string;
			fixed: {
				evening: string;
				lunch: string;
				morning: string;
				night: string;
			};
			hourly: {
				amount: string;
				repeatAfter: string;
			};
		};
		type: SelectValue;
		methodOfAdministration: NCZIReferenceSelectItem | undefined;
		periodicity: NCZIReferenceSelectItem;
		unit: SelectValue;
		treatmentTime: string;
		note: string;
		prescription: string;
	};
	patientPayment: boolean;
	useReferrer: boolean;
	referrer: {
		date: Moment;
		code: string;
		codeNZ: string;
	};
	useRepresented: boolean;
	represented: {
		date: Moment;
		code: string;
		codeNZ: string;
	};
	useRepeat: boolean;
	repeat: {
		period: number | undefined;
		count: number | undefined;
		validity: Moment;
	};
	applicationZone: SelectValue | undefined;
	excessQuantityDevice: boolean;
	approvedExemptionNumber: string | undefined;
	ventilator:
		| {
				identificationNumber: string;
				codeNZ: string;
				settings: string;
		  }
		| undefined;
	quantityLimits: SelectValue | undefined;
	indicationLimitationCode: SelectValue | undefined;
	isInternational?: boolean;
	reportedProductSubstances?: { reportedProductSubstance: string }[];
	strength?: string;
}

const dailyTypes = [
	{
		title: 'nczi.electronicPrescription.detail.dosage.daily.fixed.title',
		value: 'fixed',
	},
	{
		title: 'nczi.electronicPrescription.detail.dosage.daily.hourly.title',
		value: 'hourly',
	},
];

const medicamentType = [
	{
		title: 'nczi.electronicPrescription.detail.medicamentType.medicament',
		value: 'medicament',
	},
	{
		title: 'nczi.electronicPrescription.detail.medicamentType.ipl',
		value: 'ipl',
	},
];

interface Props {
	onClose?: (needReload: boolean) => void;
	onShowPackageInsert: (url: string) => void;
	workplaceId: string;
}

const decimalTransform = (value: string) => {
	if (!value) {
		return value;
	}

	const flValue = parseFloat(value.replace(',', '.'));
	return isNaN(flValue) ? '0' : flValue.toString();
};

const validationSchema = yup.object({
	reportedDiagnosis: NCZIReferenceValidator(
		'nczi.electronicPrescription.detail.diagnosisCode',
	),
	product: yup.mixed().when('medicamentType', {
		is: 'medicament',
		then: NCZIProductValidator(
			'nczi.electronicPrescription.detail.productCode',
		),
	}),
	deviceUnit: yup.mixed().when('product', {
		is: (value: NCZIProductSelectItem) => {
			return value?.value?.type === 'medicalDevice';
		},
		then: NCZIReferenceValidator(
			'nczi.electronicPrescription.detail.medicalDeviceUnit',
		),
	}),
	deviceDescription: yup.mixed().when('product', {
		is: (value: NCZIProductSelectItem) => {
			return value?.value?.tailored || false;
		},
		then: yup
			.string()
			.max(1800, 'nczi.validation.max1800')
			.required('nczi.electronicPrescription.detail.medicalDeviceDescription'),
	}),
	deviceJustification: yup.mixed().when('product', {
		is: (value: NCZIProductSelectItem) => {
			return (
				value?.value?.tailored ||
				false ||
				value?.value?.type === 'medicalDevice'
			);
		},
		then: yup
			.string()
			.max(1800, 'nczi.validation.max1800')
			.required(
				'nczi.electronicPrescription.detail.medicalDeviceJustification',
			),
	}),
	iplType: yup.mixed().when('medicamentType', {
		is: 'ipl',
		then: NCZIInsuranceMedicamentValidator(
			'nczi.electronicPrescription.detail.productCode',
		),
	}),
	ipl: yup.mixed().when('medicamentType', {
		is: 'ipl',
		then: yup.object({
			composition: yup
				.string()
				.required('nczi.electronicPrescription.detail.ipl.composition'),
			prepareInstruction: yup
				.string()
				.required('nczi.electronicPrescription.detail.ipl.prepareInstruction'),
			useInstruction: yup
				.string()
				.required('nczi.electronicPrescription.detail.ipl.useInstruction'),
		}),
	}),
	reportedQuantity: yup
		.number()
		.transform(value => (isNaN(value) ? 0 : value))
		.min(1, 'nczi.electronicPrescription.detail.quantity')
		.required('nczi.electronicPrescription.detail.quantity'),
	dosage: yup.object({
		type: yup.object({
			label: yup.string(),
			value: yup.string(),
		}),
		daily: yup.object({
			type: yup.string(),
			fixed: yup.object({
				evening: yup.string().transform(decimalTransform),
				lunch: yup.string().transform(decimalTransform),
				morning: yup.string().transform(decimalTransform),
				night: yup.string().transform(decimalTransform),
			}),
			hourly: yup.object({
				amount: yup.string().transform(decimalTransform),
				repeatAfter: yup.string().transform(decimalTransform),
			}),
		}),
		methodOfAdministration: yup.mixed().when('type', {
			is: (selectValue: SelectValue) => {
				return selectValue.value === DOSAGE_TYPE_PEV;
			},
			then: NCZIReferenceValidator(
				'nczi.electronicPrescription.detail.dosage.methodOfAdministration',
			),
		}),
		periodicity: yup.mixed().when('type', {
			is: (selectValue: SelectValue) => {
				return selectValue.value === DOSAGE_TYPE_PEV;
			},
			then: NCZIReferenceValidator(
				'nczi.electronicPrescription.detail.dosage.periodicity',
			),
		}),
		unit: yup.object({
			label: yup.string(),
			value: yup.string(),
		}),
		treatmentTime: yup.string().transform(decimalTransform),
		note: yup.string().when('type', {
			is: (selectValue: SelectValue) => {
				return selectValue.value === DOSAGE_TYPE_VAR;
			},
			then: yup
				.string()
				.required('nczi.electronicPrescription.detail.dosage.note'),
		}),
		prescription: yup.string(),
	}),
	referrer: yup.mixed().when('useReferrer', {
		is: true,
		then: yup.object({
			date: yup
				.mixed()
				.required('nczi.electronicPrescription.detail.referrer.date'),
			code: yup
				.string()
				.required('nczi.electronicPrescription.detail.referrer.code'),
			codeNZ: yup
				.string()
				.required('nczi.electronicPrescription.detail.referrer.codeNZ'),
		}),
	}),
	represented: yup.mixed().when('useRepresented', {
		is: true,
		then: yup.object({
			code: yup
				.string()
				.required('nczi.electronicPrescription.detail.represented.code'),
			codeNZ: yup
				.string()
				.required('nczi.electronicPrescription.detail.represented.codeNZ'),
		}),
	}),
	repeat: yup.mixed().when('useRepeat', {
		is: true,
		then: yup.object({
			period: yup
				.number()
				.min(1)
				.required('nczi.electronicPrescription.detail.repeat.period'),
			count: yup
				.number()
				.min(1)
				.required('nczi.electronicPrescription.detail.repeat.count'),
		}),
	}),
	isInternational: yup.boolean().notRequired(),
	reportedProductSubstances: yup
		.array()
		.of(
			yup.object({
				reportedProductSubstance: yup
					.string()
					.required(
						'nczi.electronicPrescription.detail.reportedProductSubstanceValidation',
					),
			}),
		)
		.when('isInternational', {
			is: true,
			then: schema =>
				schema
					.min(
						1,
						'nczi.electronicPrescription.detail.reportedProductSubstancesValidation',
					)
					.required(
						'nczi.electronicPrescription.detail.reportedProductSubstancesValidation',
					),
			otherwise: schema => schema.notRequired(),
		}),
	strength: yup
		.string()
		.transform(value => (value?.trim() ? value : undefined))
		.when('isInternational', {
			is: true,
			then: schema =>
				schema.required(
					'nczi.electronicPrescription.detail.strengthValidation',
				),
			otherwise: schema => schema.notRequired(),
		}),
});

const ElectronicPrescriptionCreateForm: React.FC<Props> = props => {
	const { onClose, onShowPackageInsert, workplaceId } = props;
	const { patientId, caseId } = usePatientTreatmentContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(true);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			hideSuccess: true,
			hideError: true,
			onFinish: (id, result) => {
				console.log({ id, result });
				if (!insuranceMedicamentVerified) {
					setInsuranceMedicamentVerified(true);
					if (typeof result?.medicaments?.[0] === 'undefined') {
						showNotificationAlert({
							content: t('nczi.verifyMedicament.uncategorized'),
						});
					}
				} else if (!interactionVerified) {
					if (typeof result.interactions !== 'undefined') {
						setInteractionModalContent(result.interactions as string);
						setInteractionModalOpened(true);
					}
					setInteractionVerified(true);
				} else {
					setCreatedPrescriptionId(id);
					setCreatedPrescriptionResult(result);
					setCreatedPrescriptionDetailOpened(true);
				}
			},
			onError: (id, message) => {
				if (!insuranceMedicamentVerified) {
					setInsuranceMedicamentVerified(true);
				} else if (!interactionVerified) {
					if (
						message.indexOf('E000000079') !== -1 ||
						message.indexOf('CHYBA #5001') !== -1 ||
						message.indexOf('neexistujú dáta pre vytvorenie výstupu') !== -1
					) {
						showNotificationSuccess({
							content: t('nczi.interactions.notFound'),
						});
					}
					setInteractionVerified(true);
				} else {
					if (message === 'Ve čtečce není vložena karta') {
						setWithoutCardShow(true);
					} else {
						setCreateErrorMessage(message);
						setForceRetryShow(true);
					}
				}
			},
		});
	const { patientBirthNumber, patientInsurance, patientInsuranceNonPayer } =
		useNCZIContext();
	const { user } = useLoadProfile({
		userId: patientId,
		loadVerifyInformation: false,
		loadUserRelations: false,
		loadHealthCard: false,
	});
	const [getReferenceItem] = useLazyNcziRefrenceItemQuery();
	const [getReferenceItemByAbbr] = useLazyNcziRefrenceItemByAbbrQuery();
	const [getReferenceItemsByCode] = useLazyNcziRefrenceItemsByCodeQuery();
	const [getListEnumItems] = useLazyNcziListEnumItemsQuery();
	const [dosageUnitItems, setDosageUnitItems] = useState<SelectValue[]>([]);
	const [activeDosageUnitItems, setActiveDosageUnitItems] = useState<
		SelectValue[]
	>([]);
	const [dosageTypes, setDosageTypes] = useState<SelectValue[]>([]);
	const [activeDosageTypes, setActiveDosageTypes] = useState<SelectValue[]>([]);
	const [multiplePrescriptionDisabled, setMultiplePrescriptionDisabled] =
		useState<boolean>(true);
	const [excessQuantityDeviceDisabled, setExcessQuantityDeviceDisabled] =
		useState<boolean>(true);
	const [interactionModalOpened, setInteractionModalOpened] =
		useState<boolean>(false);
	const [interactionModalContent, setInteractionModalContent] =
		useState<string>('');
	const [interactionVerified, setInteractionVerified] =
		useState<boolean>(false);
	const [forceRetryShow, setForceRetryShow] = useState<boolean>(false);
	const [withoutCardShow, setWithoutCardShow] = useState<boolean>(false);
	const [createErrorMessage, setCreateErrorMessage] = useState<string>('');
	const [createdPrescriptionDetailOpened, setCreatedPrescriptionDetailOpened] =
		useState<boolean>(false);
	const [createdPrescriptionId, setCreatedPrescriptionId] =
		useState<string>('');
	const [createdPrescriptionResult, setCreatedPrescriptionResult] =
		useState<any>({});
	const [tmpCommand, setTmpCommand] = useState<Command | undefined>(undefined);
	const [insuranceMedicamentVerified, setInsuranceMedicamentVerified] =
		useState<boolean>(false);
	const [quantityLimits, setQuantityLimits] = useState<string[]>([]);
	const [ioOptions, setIoOptions] = useState<
		IndicationLimitationCodeSelectOption[]
	>([]);
	const [ioNeeded, setIoNeeded] = useState<boolean>(false);
	const [needVentilator, setNeedVentilator] = useState<boolean>(false);
	const [productPilList, setProductPilList] = useState<string[] | undefined>(
		undefined,
	);

	const formMethods = useForm<ElectronicPrescriptionCreateFormValues>({
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const product = formMethods.watch('product');
	const medicamentTypeSelect = formMethods.watch('medicamentType');
	const reportedQuantity = formMethods.watch('reportedQuantity');
	const dosageType = formMethods.watch('dosage.type');
	const dosageDailyType = formMethods.watch('dosage.daily.type');
	const dosageDailyFixedEvening = formMethods.watch(
		'dosage.daily.fixed.evening',
	);
	const dosageDailyFixedLunch = formMethods.watch('dosage.daily.fixed.lunch');
	const dosageDailyFixedMorning = formMethods.watch(
		'dosage.daily.fixed.morning',
	);
	const dosageDailyFixedNight = formMethods.watch('dosage.daily.fixed.night');
	const dosageDailyHourlyAmount = formMethods.watch(
		'dosage.daily.hourly.amount',
	);
	const dosageDailyHourlyRepeatAfter = formMethods.watch(
		'dosage.daily.hourly.repeatAfter',
	);
	const dosageMethodOfAdministration = formMethods.watch(
		'dosage.methodOfAdministration',
	);
	const dosagePeriodicity = formMethods.watch('dosage.periodicity');
	const dosageUnit = formMethods.watch('dosage.unit');
	const dosageTreatmentTime = formMethods.watch('dosage.treatmentTime');
	const dosageNote = formMethods.watch('dosage.note');
	const useRepeat = formMethods.watch('useRepeat');
	const repeatPeriod = formMethods.watch('repeat.period');
	const repeatCount = formMethods.watch('repeat.count');

	const resolveProductType = (): null | string => {
		return product?.value?.type ?? null;
	};

	useEffectAsync(async () => {
		const productType = resolveProductType();
		if (medicamentTypeSelect !== 'medicament') {
			setInsuranceMedicamentVerified(true);
			setExcessQuantityDeviceDisabled(true);
			setQuantityLimits([]);
			setIoOptions([]);
			setIoNeeded(false);
			setNeedVentilator(false);
		} else {
			if (productType === 'medicament') {
				setActiveDosageTypes(dosageTypes);
				setExcessQuantityDeviceDisabled(true);
			} else if (productType === 'medicalDevice') {
				const filteredItems = dosageTypes.filter(item => {
					return (
						[DOSAGE_TYPE_VAR, DOSAGE_TYPE_PEV].indexOf(
							item?.value.toString(),
						) !== -1
					);
				});
				setActiveDosageTypes(filteredItems);
				if (
					[DOSAGE_TYPE_VAR, DOSAGE_TYPE_PEV].indexOf(
						dosageType?.value?.toString(),
					) === -1
				) {
					formMethods.setValue('dosage.type', filteredItems[0]);
				}
				setExcessQuantityDeviceDisabled(false);
			} else {
				setExcessQuantityDeviceDisabled(true);
				setActiveDosageTypes(dosageTypes);
			}

			if (productType !== null) {
				let dosageUnitFilterItems: string[] = [];
				let defaultUnit = '';

				if (product?.value?.additionalName && productType !== 'medicalDevice') {
					const code = product?.value?.additionalName
						.substring(0, 3)
						.toUpperCase();
					const { item: referenceItemRelation } = await getReferenceItem({
						oid: NCZI_OID.DOSAGE_RELATION_UNIT,
						code,
					}).unwrap();

					if (referenceItemRelation) {
						const referenceParams = referenceItemRelation.params;
						if (referenceParams.relation) {
							dosageUnitFilterItems = referenceParams.relation;
						}
						defaultUnit = referenceParams.default ?? '';
					}
				}

				let disableMultiplePrescription = false;
				if (productType === 'medicament' && product?.value.code) {
					disableMultiplePrescription = true;
					if (
						product?.value &&
						!product?.value?.opl &&
						!product?.value?.antibioticsChemotherapy
					) {
						disableMultiplePrescription = false;
					}

					console.log({ productData: product?.value });
				}

				if (disableMultiplePrescription) {
					formMethods.setValue('useRepeat', false);
				}
				setMultiplePrescriptionDisabled(disableMultiplePrescription);

				const tmpDosageUnitItems = dosageUnitItems.filter(item => {
					return dosageUnitFilterItems.length
						? dosageUnitFilterItems.indexOf(item.value.toString()) !== -1
						: true;
				});
				setActiveDosageUnitItems(tmpDosageUnitItems);

				const foundDefault = tmpDosageUnitItems.find(item => {
					return item.value == defaultUnit;
				});
				if (foundDefault) {
					formMethods.setValue('dosage.unit', foundDefault);
				}

				if (product?.value?.atcCode) {
					const { item: referenceAtc } = await getReferenceItemByAbbr({
						oid: NCZI_OID.ATC_CLASSIFICATION_SYSTEM,
						abbr: product.value.atcCode,
					}).unwrap();

					if (referenceAtc) {
						formMethods.setValue('reportedATC', {
							value: {
								displayName: referenceAtc.name,
								code: referenceAtc.code,
								scheme: {
									oid: referenceAtc.enum.oid,
									version: referenceAtc.enum.version,
								},
							},
							label: referenceAtc.abbreviation + ': ' + referenceAtc.name,
						});
					} else {
						formMethods.setValue('reportedATC', undefined);
					}
				}
				if (product?.value?.atcAddon) {
					formMethods.setValue('atcAddon', product?.value?.atcAddon);
				} else {
					formMethods.setValue('atcAddon', '');
				}

				setQuantityLimits(product?.value?.quantityLimit || []);
				setNeedVentilator(product?.value?.ventilator || false);
				setIoNeeded(product?.value?.ioNeeded || false);
				const tmpIoOptions: IndicationLimitationCodeSelectOption[] = [];
				let tmpIoOptionsCodes: string[] = [];
				if (product?.value?.ioNeeded) {
					tmpIoOptionsCodes = product?.value?.ioOptions || [];
					const tmpIoDescs: { code: string; description: string }[] = [];
					if (tmpIoOptionsCodes.length) {
						const {
							items: { enumItems },
						} = await getReferenceItemsByCode({
							oid: NCZI_OID.INDICATION_LIMITATION,
							code: tmpIoOptionsCodes,
						}).unwrap();
						for (const enumItem of enumItems) {
							tmpIoDescs.push({
								code: enumItem.code,
								description: enumItem.description,
							});
						}
					}
					tmpIoOptionsCodes?.forEach(item => {
						tmpIoOptions.push({
							value: item,
							label: item,
							description:
								tmpIoDescs?.find(desc => desc.code === item)?.description ??
								undefined,
						});
					});
					tmpIoOptions.push({
						value: 'I00000',
						label: 'I00000',
					});
				}

				setIoOptions(tmpIoOptions);

				if (product?.value?.pilList) {
					setProductPilList(product.value.pilList);
				} else {
					setProductPilList(undefined);
				}

				console.log({ selProd: product?.value });
			} else {
				setQuantityLimits([]);
				setNeedVentilator(false);
				setIoOptions([]);
				setIoNeeded(false);
				setProductPilList(undefined);
			}

			setInteractionVerified(false);
			await verifyInsuranceMedicament();
		}
	}, [product, medicamentTypeSelect]);

	useEffectAsync(async () => {
		const productType = resolveProductType();
		if (!useRepeat || !productType || !repeatPeriod || !repeatCount) {
			return;
		}

		const medicationDeadline = productType === 'medicalDevice' ? 30 : 7;
		let calculation =
			repeatPeriod * (repeatCount - 1) +
			medicationDeadline +
			3 * (repeatCount - 1);
		if (calculation > 364) {
			calculation = 364;
		}
		if (calculation < medicationDeadline) {
			calculation = medicationDeadline;
		}

		const validity = moment().add(calculation + 1, 'days');
		validity.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
		formMethods.setValue('repeat.validity', validity);
	}, [product, useRepeat, repeatPeriod, repeatCount]);

	useEffectAsync(async () => {
		const dosageTypeCode = dosageType?.value?.toString();
		let prescriptionText = 'D.S.';

		if (dosageTypeCode === DOSAGE_TYPE_PEV) {
			if (dosageDailyType === 'fixed') {
				prescriptionText +=
					' ' +
					[
						decimalTransform(dosageDailyFixedMorning).replace('.', ','),
						decimalTransform(dosageDailyFixedLunch).replace('.', ','),
						decimalTransform(dosageDailyFixedEvening).replace('.', ','),
						decimalTransform(dosageDailyFixedNight).replace('.', ','),
					].join('-');
				prescriptionText +=
					', ' +
					(dosageUnit?.value ? dosageUnit?.label : '') +
					' ' +
					(dosagePeriodicity?.value?.displayName ?? '') +
					' ' +
					(dosageMethodOfAdministration?.value?.displayName ?? '');
			} else if (dosageDailyType === 'hourly') {
				prescriptionText +=
					' á ' +
					decimalTransform(dosageDailyHourlyRepeatAfter).replace('.', ',') +
					' hod. ' +
					decimalTransform(dosageDailyHourlyAmount).replace('.', ',') +
					' ';
				prescriptionText +=
					(dosageUnit?.value ? dosageUnit?.label : '') +
					' ' +
					(dosagePeriodicity?.value?.displayName ?? '') +
					' ' +
					(dosageMethodOfAdministration?.value?.displayName ?? '');
			}
		} else if (dosageTypeCode !== DOSAGE_TYPE_VAR) {
			prescriptionText += ' ' + dosageType?.label;
		}

		if (dosageNote) {
			prescriptionText += ' ' + dosageNote;
		}
		if (dosageTreatmentTime && decimalTransform(dosageTreatmentTime)) {
			prescriptionText +=
				', po dobu ' +
				decimalTransform(dosageTreatmentTime).replace('.', ',') +
				' ' +
				(decimalTransform(dosageTreatmentTime) === '1' ? 'dňa' : 'dní');
		}

		formMethods.setValue('dosage.prescription', prescriptionText);
	}, [
		dosageDailyType,
		reportedQuantity,
		dosageDailyFixedEvening,
		dosageDailyFixedLunch,
		dosageType,
		dosageDailyFixedMorning,
		dosageDailyFixedNight,
		dosageDailyHourlyAmount,
		dosageDailyHourlyRepeatAfter,
		dosageMethodOfAdministration,
		dosagePeriodicity,
		dosageUnit,
		dosageTreatmentTime,
		dosageNote,
	]);

	const verifyInsuranceMedicament = async () => {
		setInsuranceMedicamentVerified(false);

		if (product?.value?.type && product.value.type !== 'medicament') {
			setInsuranceMedicamentVerified(true);
		} else if (product?.value?.code) {
			const searchCommand = SearchMedicaments({
				insurance: patientInsurance!,
				medicamentCode: [product.value.code],
			});

			await processCommand(searchCommand);
		}
	};

	const handleForceWrite = async (reason: string) => {
		const cmd = { ...tmpCommand } as Command;
		const params = {
			...cmd.params,
		} as WritePatientElectronicPrescriptionParams;
		params.forcedEntry = true;
		params.forcedEntryReason = reason;

		const command = WritePatientElectronicPrescription(params);

		setForceRetryShow(false);
		await processCommand(command);
	};

	const handleWriteWithoutCard = async () => {
		const cmd = { ...tmpCommand } as Command;
		const params = {
			...cmd.params,
		} as WritePatientElectronicPrescriptionParams;
		params.withoutCard = true;

		const command = WritePatientElectronicPrescription(params);
		setTmpCommand(command);

		setWithoutCardShow(false);
		await processCommand(command);
	};

	const handleSubmit = async (
		values: ElectronicPrescriptionCreateFormValues,
	) => {
		let countryCode = 'SK';
		if (user?.meta?.state?.code) {
			const { item: countryItem } = await getReferenceItem({
				oid: NCZI_OID.COUNTRY,
				code: user?.meta?.state?.code,
			}).unwrap();
			if (countryItem?.params?.['6']) {
				countryCode = countryItem.params['6'];
			}
		}

		let command: Command;
		if (interactionVerified) {
			command = WritePatientElectronicPrescription({
				caseId,
				birthNumber: patientBirthNumber!,
				insurance: patientInsurance!,
				urgentEmergency: values.urgentEmergency,
				nonPayer: patientInsuranceNonPayer ?? false,
				prohibitionOfReplacement: values.prohibitionOfReplacement,
				prohibitionOfReplacementCode:
					values.prohibitionOfReplacementCode?.value?.code || undefined,
				forcedEntry: false,
				productOnException: values.productOnException,
				newbornToParentBN: values.newbornToParentBN,
				permanentlyAffected: values.permanentlyAffected,
				reportedDate: values.reportedDate.format('YYYY-MM-DD'),
				reportedQuantity: values.reportedQuantity,
				reportedDiagnosis: values.reportedDiagnosis?.value as NCZIReference,
				reportedProductCode: (values.medicamentType === 'ipl'
					? values.iplType?.value?.code
					: values.product?.value?.code) as string,
				reportedProductName: (values.medicamentType === 'ipl'
					? values.iplType?.value?.displayName
					: values.product?.value?.name) as string,
				reportedProductAdditionalName: (values.medicamentType === 'ipl'
					? values.ipl.composition
					: values.product?.value?.additionalName) as string,
				reportedATC: values.reportedATC?.value,
				deviceUnit: values.deviceUnit?.value || undefined,
				deviceDescription: values.deviceDescription || undefined,
				deviceJustification: values.deviceJustification || undefined,
				atcAddon: values.atcAddon,
				meddiPatient: {
					id: user?.id ?? '',
					fullName: user?.profile?.fullName ?? '',
					fullAddress: user?.profile?.address?.fullAddress ?? '',
				},
				dosage: {
					type: values.dosage.type.value as string,
					note: values.dosage.note,
					prescription: values.dosage.prescription,
					methodOfAdministration:
						values.dosage.type.value === DOSAGE_TYPE_PEV
							? values.dosage.methodOfAdministration?.value
							: undefined,
					dosageUnit:
						values.dosage.type.value === DOSAGE_TYPE_PEV
							? (values.dosage.unit?.value as NCZIReference)
							: undefined,
					periodicity:
						values.dosage.type.value === DOSAGE_TYPE_PEV
							? (values.dosage.periodicity?.value as NCZIReference)
							: undefined,
					treatmentTime: values.dosage.treatmentTime || undefined,
					dailyDosage:
						values.dosage.type.value === DOSAGE_TYPE_PEV
							? {
									partsOfDay:
										values.dosage.daily.type === 'fixed'
											? [
													values.dosage.daily.fixed.morning,
													values.dosage.daily.fixed.lunch,
													values.dosage.daily.fixed.evening,
													values.dosage.daily.fixed.night,
											  ]
											: undefined,
									hourly:
										values.dosage.daily.type !== 'fixed'
											? {
													amount: values.dosage.daily.hourly.amount,
													perHours: values.dosage.daily.hourly.repeatAfter,
											  }
											: undefined,
							  }
							: undefined,
				},
				ipl:
					values.medicamentType === 'ipl'
						? {
								composition: values.ipl.composition,
								prepareInstruction: values.ipl.prepareInstruction,
								useInstruction: values.ipl.useInstruction,
						  }
						: undefined,
				referrer: values.useReferrer
					? {
							date: values.referrer.date.format('YYYY-MM-DD'),
							code: values.referrer.code,
							codeNZ: values.referrer.codeNZ,
					  }
					: undefined,
				represented: values.useRepresented
					? {
							code: values.represented.code,
							codeNZ: values.represented.codeNZ,
					  }
					: undefined,
				repeat: values.useRepeat
					? {
							period: values.repeat.period!,
							count: values.repeat.count!,
							validity: values.repeat.validity.format(),
					  }
					: undefined,
				pairOrganZone: values.product?.value?.code?.startsWith('I')
					? values.applicationZone?.value?.toString()
					: undefined,
				patientPaymentReason: values.patientPayment ? 'JASAM' : undefined,
				insurerGender: user?.profile?.gender === 'female' ? 'Z' : 'M',
				insurerState: countryCode,
				excessQuantityDevice: excessQuantityDeviceDisabled
					? false
					: values.excessQuantityDevice,
				approvedExemptionNumber: values.productOnException
					? values.approvedExemptionNumber
					: undefined,
				ventilator:
					needVentilator && values.ventilator
						? {
								identificationNumber: values.ventilator?.identificationNumber,
								codeNZ: values.ventilator?.codeNZ,
								settings: values.ventilator?.settings,
						  }
						: undefined,
				quantityLimitDieteticFood:
					values.quantityLimits?.value?.toString().split('/')[0] || undefined,
				indicationLimitationCode: ioNeeded
					? values.indicationLimitationCode?.value.toString() || undefined
					: undefined,
				disableMultiplePrescription: values.product?.value?.opl || false,
				isInternational: values.isInternational,
				reportedProductSubstances: values.reportedProductSubstances?.map(
					item => item.reportedProductSubstance,
				),
				workplaceId,
				strength: values.strength,
			});
		} else {
			command = VerifyPatientInteractions({
				birthNumber: patientBirthNumber!,
				insurance: patientInsurance!,
				medicamentCode: [
					(values.medicamentType === 'ipl'
						? values.iplType?.value?.code
						: values.product?.value?.code) as string,
				],
				dateFrom: moment().subtract(1, 'year').format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
			});
		}

		setTmpCommand(command);
		await processCommand(command);
	};

	const closeInteractionModal = () => {
		setInteractionModalOpened(false);
	};

	const handleCloseCreatedPrescriptionDetail = () => {
		setCreatedPrescriptionDetailOpened(false);
		onClose?.(true);
	};

	useEffectAsync(async () => {
		const now = getDate();

		const { item: referenceItem } = await getReferenceItem({
			oid: NCZI_OID.DOSAGE_TYPE,
			code: DOSAGE_TYPE_PEV,
		}).unwrap();

		const { item: referenceItemPeriodicity } = await getReferenceItem({
			oid: NCZI_OID.DOSAGE_PERIODICITY,
			code: 'D',
		}).unwrap();

		const { items: dosageTypeItems } = await getListEnumItems({
			oid: NCZI_OID.DOSAGE_TYPE,
			take: 99,
		}).unwrap();

		const tmpDosageTypes = (dosageTypeItems?.data ?? []).map(item => ({
			label: item?.name ?? '',
			value: item?.code ?? '',
		}));
		setDosageTypes(tmpDosageTypes);
		setActiveDosageTypes(tmpDosageTypes);

		const { items: dosageUnitItems } = await getListEnumItems({
			oid: NCZI_OID.DOSAGE_UNIT,
			take: 999,
		}).unwrap();
		const tmpItems = (dosageUnitItems?.data ?? []).map(item => ({
			label: item?.name || '',
			value: item?.code || '',
		}));
		setDosageUnitItems(tmpItems);
		setActiveDosageUnitItems(tmpItems);

		const tempDefaultValues = {
			reportedDate: now,
			medicamentType: 'medicament',
			dosage: {
				daily: {
					type: 'fixed',
					fixed: {
						evening: '0',
						lunch: '0',
						morning: '0',
						night: '0',
					},
					hourly: {
						amount: '',
						repeatAfter: '',
					},
				},
				type: referenceItem
					? {
							label: referenceItem.name,
							value: referenceItem.code,
					  }
					: undefined,
				periodicity: referenceItemPeriodicity
					? {
							label: referenceItemPeriodicity.name,
							value: {
								code: referenceItemPeriodicity.code,
								displayName: referenceItemPeriodicity.name,
								scheme: {
									oid: referenceItemPeriodicity.enum?.oid,
									version: referenceItemPeriodicity.enum?.version,
								},
							},
					  }
					: undefined,
				treatmentTime: '',
				note: '',
			},
			repeat: {
				validity: getDate().add(31, 'days'),
			},
		};
		formMethods.reset(tempDefaultValues);
		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	if (forceRetryShow) {
		return (
			<ElectronicPrescriptionCreateFail
				onConfirm={handleForceWrite}
				onDecline={() => {
					setForceRetryShow(false);
				}}
				errorMessage={createErrorMessage}
			/>
		);
	}

	if (withoutCardShow) {
		return (
			<ElectronicPrescriptionCreateWithoutCard
				onConfirm={handleWriteWithoutCard}
				onDecline={() => {
					setWithoutCardShow(false);
				}}
			/>
		);
	}

	return (
		<div style={{ width: '100%' }}>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<FormWrapper formMethods={formMethods}>
					<FormControl
						label='nczi.electronicPrescription.detail.date'
						name='reportedDate'
						isRequired>
						<DatePicker />
					</FormControl>
					<InputWrapper>
						<NCZIReferenceSearchSelectField
							label='nczi.electronicPrescription.detail.diagnosisCode'
							name='reportedDiagnosis'
							oid={NCZI_OID.DIAGNOSIS}
							showCode
							isRequired
							portal={true}
						/>
					</InputWrapper>
					<FormTitle
						tx='nczi.electronicPrescription.detail.medicamentType.title'
						type='default'
					/>
					<InputWrapper>
						<RadioListField
							name='medicamentType'
							data={medicamentType}
							inLine
							isRequired
						/>
					</InputWrapper>
					<FormCondition controlName='medicamentType' equal={'medicament'}>
						<InputWrapper>
							<NCZIProductSearchSelectField
								label='nczi.electronicPrescription.detail.productCode'
								name='product'
								isRequired={true}
								portal={true}
							/>
						</InputWrapper>
						{needVentilator && (
							<FormContainer name={'ventilator'} settings={{ showLine: true }}>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.ventilator.identificationNumber'
										name='identificationNumber'
										isRequired
									/>
								</InputWrapper>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.ventilator.codeNZ'
										name='codeNZ'
										isRequired
									/>
								</InputWrapper>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.ventilator.settings'
										name='settings'
										isRequired
									/>
								</InputWrapper>
							</FormContainer>
						)}
						{quantityLimits.length > 0 && (
							<InputWrapper>
								<SelectField
									label='nczi.electronicPrescription.detail.quantityLimits'
									name='quantityLimits'
									options={quantityLimits.map(limit => ({
										value: limit,
										label: limit,
									}))}
									isRequired
								/>
							</InputWrapper>
						)}
						{ioNeeded && (
							<InputWrapper>
								<IndicationLimitationCodeSelectField
									label='nczi.electronicPrescription.detail.indicationLimitationCode'
									name='indicationLimitationCode'
									options={ioOptions}
									isRequired
								/>
							</InputWrapper>
						)}
						<FormCondition
							controlName={'product'}
							rules={[
								{ formControlKey: 'product', rule: 'filled', value: null },
								{
									formControlKey: 'product',
									rule: 'notStartsWith',
									value: ['C', 'M', 'S'],
								},
							]}>
							<InputWrapper>
								<NCZIReferenceSelectField
									label='nczi.electronicPrescription.detail.medicalDeviceUnit'
									name='deviceUnit'
									oid={NCZI_OID.MEDICAL_DEVICE_UNIT}
									isRequired
								/>
							</InputWrapper>
						</FormCondition>
						<FormCondition
							controlName={'product'}
							rules={[
								{ formControlKey: 'product', rule: 'filled', value: null },
								{ formControlKey: 'product', rule: 'startsWith', value: ['I'] },
							]}>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.medicalDeviceDescription'
									name='deviceDescription'
									isRequired
									charactersLimit={1800}
								/>
							</InputWrapper>
						</FormCondition>
						<FormCondition
							controlName={'product'}
							rules={[
								{ formControlKey: 'product', rule: 'filled', value: null },
								{
									formControlKey: 'product',
									rule: 'startsWith',
									value: ['I', 'D', 'B', 'K'],
								},
							]}>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.medicalDeviceJustification'
									name='deviceJustification'
									isRequired
									charactersLimit={1800}
								/>
							</InputWrapper>
						</FormCondition>
						<FormCondition
							controlName={'product'}
							rules={[
								{ formControlKey: 'product', rule: 'filled', value: null },
								{ formControlKey: 'product', rule: 'startsWith', value: ['I'] },
							]}>
							<InputWrapper>
								<SelectField
									label='nczi.electronicPrescription.detail.applicationZone.title'
									name='applicationZone'
									options={[
										{
											value: 'L',
											label:
												'nczi.electronicPrescription.detail.applicationZone.left',
										},
										{
											value: 'P',
											label:
												'nczi.electronicPrescription.detail.applicationZone.right',
										},
									]}
									isRequired
								/>
							</InputWrapper>
						</FormCondition>
					</FormCondition>
					<FormCondition controlName='medicamentType' equal={'ipl'}>
						<InputWrapper>
							<NCZIReferenceSelectField
								label='nczi.electronicPrescription.detail.productCode'
								name='iplType'
								oid={NCZI_OID.IPL_CODES}
								isRequired
							/>
						</InputWrapper>
						<FormContainer name={'ipl'} settings={{ showLine: true }}>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.ipl.composition'
									name='composition'
									isRequired={true}
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.ipl.prepareInstruction'
									name='prepareInstruction'
									isRequired={true}
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.ipl.useInstruction'
									name='useInstruction'
									isRequired={true}
								/>
							</InputWrapper>
						</FormContainer>
					</FormCondition>
					{insuranceMedicamentVerified && (
						<>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.quantity'
									name='reportedQuantity'
									isRequired={true}
								/>
							</InputWrapper>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.electronicPrescription.detail.atc'
									name='reportedATC'
									oid={NCZI_OID.ATC_CLASSIFICATION_SYSTEM}
									isRequired
									showAbbr
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.electronicPrescription.detail.atcAddon'
									name='atcAddon'
									isRequired={false}
								/>
							</InputWrapper>
							<FormTitle
								tx='nczi.electronicPrescription.detail.dosage.title'
								type='default'
							/>
							<FormContainer name={'dosage'} settings={{ showLine: true }}>
								<InputWrapper>
									<SelectField
										label='nczi.electronicPrescription.detail.dosage.type'
										name='type'
										options={activeDosageTypes}
										isRequired
									/>
								</InputWrapper>
								<FormCondition controlName='type' equal={DOSAGE_TYPE_PEV}>
									<FormTitle
										tx='nczi.electronicPrescription.detail.dosage.dailyType'
										type='default'
									/>
									<FormContainer name={'daily'} settings={{ showLine: true }}>
										<InputWrapper>
											<RadioListField name='type' data={dailyTypes} inLine />
										</InputWrapper>
										<FormCondition controlName='type' equal='fixed'>
											<FormContainer name='fixed'>
												<div className={'row'}>
													<div className={'col-3'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.fixed.morning'
																name='morning'
																isRequired
															/>
														</InputWrapper>
													</div>
													<div className={'col-3'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.fixed.lunch'
																name='lunch'
																isRequired
															/>
														</InputWrapper>
													</div>
													<div className={'col-3'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.fixed.evening'
																name='evening'
																isRequired
															/>
														</InputWrapper>
													</div>
													<div className={'col-3'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.fixed.night'
																name='night'
																isRequired
															/>
														</InputWrapper>
													</div>
												</div>
											</FormContainer>
										</FormCondition>
										<FormCondition controlName='type' equal='hourly'>
											<FormContainer name='hourly'>
												<div className={'row'}>
													<div className={'col-6'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.hourly.amount'
																name='amount'
																isRequired
															/>
														</InputWrapper>
													</div>
													<div className={'col-6'}>
														<InputWrapper>
															<InputField
																label='nczi.electronicPrescription.detail.dosage.daily.hourly.repeatAfter'
																name='repeatAfter'
																isRequired
															/>
														</InputWrapper>
													</div>
												</div>
											</FormContainer>
										</FormCondition>
									</FormContainer>
									<InputWrapper>
										<SelectField
											label='nczi.electronicPrescription.detail.dosage.unit'
											name='unit'
											options={activeDosageUnitItems}
											isRequired
										/>
									</InputWrapper>
									<InputWrapper>
										<NCZIReferenceSelectField
											label='nczi.electronicPrescription.detail.dosage.methodOfAdministration'
											name='methodOfAdministration'
											oid={NCZI_OID.DOSAGE_METHOD_ADMINISTRATION}
											isRequired
										/>
									</InputWrapper>
									<InputWrapper>
										<NCZIReferenceSelectField
											label='nczi.electronicPrescription.detail.dosage.periodicity'
											name='periodicity'
											oid={NCZI_OID.DOSAGE_PERIODICITY}
											isRequired
										/>
									</InputWrapper>
								</FormCondition>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.dosage.note'
										name='note'
										charactersLimit={150}
										isRequired
									/>
								</InputWrapper>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.dosage.treatmentTime'
										name='treatmentTime'
										isRequired
									/>
								</InputWrapper>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.dosage.transcription'
										name='prescription'
										isRequired={true}
										disabled
									/>
								</InputWrapper>
							</FormContainer>

							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.urgentEmergency'
									name='urgentEmergency'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.prohibitionOfReplacement'
									name='prohibitionOfReplacement'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<FormCondition
								controlName='prohibitionOfReplacement'
								equal={true}>
								<InputWrapper>
									<NCZIProductSearchSelectField
										label='nczi.electronicPrescription.detail.prohibitionOfReplacementCode'
										name='prohibitionOfReplacementCode'
										isRequired={true}
										portal={true}
									/>
								</InputWrapper>
							</FormCondition>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.productOnException'
									name='productOnException'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<FormCondition controlName='productOnException' equal={true}>
								<InputWrapper>
									<InputField
										label='nczi.electronicPrescription.detail.approvedExemptionNumber'
										name='approvedExemptionNumber'
										isRequired
									/>
								</InputWrapper>
							</FormCondition>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.newbornToParentBN'
									name='newbornToParentBN'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.permanentlyAffected'
									name='permanentlyAffected'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.patientPayment'
									name='patientPayment'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.excessQuantityDevice'
									name='excessQuantityDevice'
									isRequired
									firstUpper
									isDisabled={excessQuantityDeviceDisabled}
								/>
							</InputWrapper>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.useRepeat'
									name='useRepeat'
									isRequired
									firstUpper
									isDisabled={multiplePrescriptionDisabled}
								/>
							</InputWrapper>
							<FormCondition controlName='useRepeat' equal={true}>
								<FormContainer name='repeat' settings={{ showLine: true }}>
									<div className={'row'}>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.repeat.period'
													name='period'
													isRequired
												/>
											</InputWrapper>
										</div>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.repeat.count'
													name='count'
													isRequired
												/>
											</InputWrapper>
										</div>
									</div>
									<InputWrapper>
										<FormControl
											label='nczi.electronicPrescription.detail.repeat.validity'
											name='validity'
											isRequired>
											<DatePicker />
										</FormControl>
									</InputWrapper>
								</FormContainer>
							</FormCondition>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.useReferrer'
									name='useReferrer'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<FormCondition controlName='useReferrer' equal={true}>
								<FormContainer name='referrer' settings={{ showLine: true }}>
									<InputWrapper>
										<FormControl
											label='nczi.electronicPrescription.detail.referrer.date'
											name='date'
											isRequired>
											<DatePicker />
										</FormControl>
									</InputWrapper>
									<div className={'row'}>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.referrer.code'
													name='code'
													isRequired
												/>
											</InputWrapper>
										</div>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.referrer.codeNZ'
													name='codeNZ'
													isRequired
												/>
											</InputWrapper>
										</div>
									</div>
								</FormContainer>
							</FormCondition>
							<InputWrapper>
								<CheckboxField
									tx='nczi.electronicPrescription.detail.useRepresented'
									name='useRepresented'
									isRequired
									firstUpper
								/>
							</InputWrapper>
							<FormCondition controlName='useRepresented' equal={true}>
								<FormContainer name='represented' settings={{ showLine: true }}>
									<div className={'row'}>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.represented.code'
													name='code'
													isRequired
												/>
											</InputWrapper>
										</div>
										<div className={'col-6'}>
											<InputWrapper>
												<InputField
													label='nczi.electronicPrescription.detail.represented.codeNZ'
													name='codeNZ'
													isRequired
												/>
											</InputWrapper>
										</div>
									</div>
								</FormContainer>
							</FormCondition>
							<InputWrapper>
								<CheckboxField
									name='isInternational'
									tx='nczi.electronicPrescription.detail.isInternational'
								/>
								<FormCondition controlName='isInternational' equal={true}>
									{productPilList?.map(pilItem => (
										<LinkButton
											title='nczi.electronicPrescription.detail.showPackageList'
											theme={Theme.BLUE}
											onClick={() =>
												onShowPackageInsert(
													`https://cdn.meddi.com/documents/eu/sk/sukl/spc-pil/${pilItem}`,
												)
											}
											style={{ margin: '10px 0' }}
										/>
									))}
									<InputField
										name='strength'
										label='nczi.electronicPrescription.detail.strength'
										isRequired
									/>
									<Text
										className={styles.boldInformation}
										tx='nczi.electronicPrescription.detail.reportedProductSubatencesInfo'
									/>
									<FormMultiplier
										name='reportedProductSubstances'
										addButtonTitle='nczi.electronicPrescription.detail.reportedProductSubstancesAdd'
										createDefault={1}>
										<InputWrapper>
											<InputField
												name='reportedProductSubstance'
												label='nczi.electronicPrescription.detail.reportedProductSubstance'
												isRequired
											/>
											<FormMultiplierRemoveButton>
												<LinkButton
													title='base.shared.remove'
													theme={Theme.RED}
												/>
											</FormMultiplierRemoveButton>
										</InputWrapper>
									</FormMultiplier>
								</FormCondition>
							</InputWrapper>
							<div className='mt-4'>
								<SubmitButton
									title={
										interactionVerified
											? 'nczi.savePrescription'
											: 'nczi.verifyInteractions'
									}
									onSubmit={handleSubmit}
								/>
							</div>
						</>
					)}
				</FormWrapper>
			)}
			<NcziHtmlModalViewer
				opened={interactionModalOpened}
				onClose={closeInteractionModal}
				content={interactionModalContent}
				title={'nczi.interactionModal.title'}
			/>
			<ElectronicPrescriptionCreatedDetailModal
				opened={createdPrescriptionDetailOpened}
				onClose={handleCloseCreatedPrescriptionDetail}
				messageId={createdPrescriptionId}
				result={createdPrescriptionResult}
				disableMultiplePrescription={product?.value?.opl || false}
			/>
		</div>
	);
};

export default withForm(ElectronicPrescriptionCreateForm);
